import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { Typography } from "@mui/material";
import { IconButton } from "@mui/material";
import { Delete } from "@mui/icons-material";
import { useAddBlogMutation, useUpdateBlogMutation } from "../api/blogsApi";
import { Blog } from "../types/blog";
import ReactQuill from "react-quill";

interface Props {
  open: boolean;
  setModalOpen: (open: boolean) => void;
  onSuccess?: () => void;
  blog?: Blog | null;
}

const AddEditBlog: React.FC<Props> = ({
  open,
  setModalOpen,
  onSuccess,
  blog,
}) => {
  const [formData, setFormData] = useState<{
    title: string;
    slug: string;
    author?: string;
    content: string;
    blog_image: File | null;
    images: File[];
    existingImages: Blog["images"];
    existingBlogImage?: Blog["image"];
    is_featured: boolean; // 🟢 Added
  }>({
    title: "",
    slug: "",
    author: "",
    content: "",
    blog_image: null,
    images: [],
    existingImages: [],
    existingBlogImage: undefined,
    is_featured: false, // 🟢 Added
  });

  const [addBlog] = useAddBlogMutation();
  const [updateBlog] = useUpdateBlogMutation();
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const quillRef = useRef<any>(null);

  useEffect(() => {
    if (blog) {
      setFormData({
        title: blog.title,
        slug: blog.slug,
        author: blog.author ?? "",
        content: blog.content,
        blog_image: null,
        images: [],
        existingImages: blog.images || [],
        existingBlogImage: blog.image,
        is_featured: blog.is_featured || false, // 🟢 Added
      });
    } else {
      setFormData({
        title: "",
        slug: "",
        author: "",
        content: "",
        blog_image: null,
        images: [],
        existingImages: [],
        existingBlogImage: undefined,
        is_featured: false, // 🟢 Added
      });
    }
  }, [blog, open]);

  const handleContentChange = useCallback((value: string) => {
    setFormData((prev) => ({ ...prev, content: value }));
  }, []);

  const handleBlogImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData((prev) => ({ ...prev, blog_image: file }));
  };

  const handleImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setFormData((prev) => ({
      ...prev,
      images: [...prev.images, ...files],
    }));
  };

  const removeNewImage = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const removeExistingImage = (id: number) => {
    setFormData((prev) => ({
      ...prev,
      existingImages: prev.existingImages.filter((img) => img.id !== id),
    }));
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    const fd = new FormData();
    fd.append("title", formData.title);
    fd.append("slug", formData.slug);
    if (formData.author && formData.author.trim()) {
      fd.append("author", formData.author.trim());
    }
    fd.append("content", formData.content);
    fd.append("is_featured", formData.is_featured ? "1" : "0"); // 🟢 Added
    if (formData.blog_image) {
      fd.append("blog_image", formData.blog_image);
    }
    formData.images.forEach((file, i) => fd.append(`images[${i}]`, file));
    formData.existingImages.forEach((img, i) => {
      if (img.id) {
        fd.append(`existing_image_ids[${i}]`, img.id.toString());
      }
    });

    try {
      if (blog?.id) {
        await updateBlog({ id: blog.id, body: fd }).unwrap();
      } else {
        await addBlog({ body: fd }).unwrap();
      }
      setModalOpen(false);
      onSuccess?.();
      setSubmissionError(null);
    } catch (err) {
      console.error("Error saving blog:", err);
      setSubmissionError("Failed to save blog. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const modules: Record<string, any> = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ align: [] }],
      ["link", "image"],
      ["clean"],
    ],
  };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "link",
    "image",
  ];

  useEffect(() => {
    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;
    const root = editor.root as HTMLElement;
    const onPaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const imageItems = Array.from(items).filter(
        (item) => item.kind === "file" && item.type.startsWith("image/")
      );
      if (!imageItems.length) return;
      e.preventDefault();
      imageItems.forEach((item) => {
        const file = item.getAsFile();
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
          const range = editor.getSelection(true);
          const index = range ? range.index : editor.getLength();
          editor.insertEmbed(index, "image", reader.result as string, "user");
          editor.setSelection(index + 1, 0);
        };
        reader.readAsDataURL(file);
      });
    };
    root.addEventListener("paste", onPaste as unknown as EventListener);
    return () => {
      root.removeEventListener("paste", onPaste as unknown as EventListener);
    };
  }, [open]);

  const [selectedImage, setSelectedImage] = useState<HTMLImageElement | null>(null);
  useEffect(() => {
    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;
    const handler = (range: any) => {
      if (!range) {
        setSelectedImage(null);
        return;
      }
      const [leaf] = editor.getLeaf(range.index);
      const node = (leaf && (leaf as any).domNode) as HTMLElement | undefined;
      if (node && node.tagName === "IMG") {
        setSelectedImage(node as HTMLImageElement);
      } else {
        setSelectedImage(null);
      }
    };
    editor.on("selection-change", handler);
    return () => {
      editor.off?.("selection-change", handler);
    };
  }, [open]);

  const applyAlign = (value: "left" | "center" | "right") => {
    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;
    editor.format("align", value);
  };

  const applyWidth = (percent: number) => {
    if (selectedImage) {
      selectedImage.style.width = `${percent}%`;
      selectedImage.style.height = "auto";
    }
  };


  return (
    <Dialog
      open={open}
      onClose={() => setModalOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{blog ? "Edit Blog" : "Add Blog"}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          margin="dense"
          label="Author"
          value={formData.author ?? ""}
          onChange={(e) => setFormData({ ...formData, author: e.target.value })}
        />
        <TextField
          fullWidth
          margin="dense"
          label="Title"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
        />

        <Box sx={{ mt: 2, mb: 2 }}>
          <ReactQuill
            theme="snow"
            value={formData.content}
            onChange={handleContentChange}
            modules={modules}
            formats={formats}
            style={{ height: "200px" }}
            ref={quillRef}
          />
        </Box>
        {selectedImage && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
            <Typography variant="body2">Image tools:</Typography>
            <Button size="small" variant="outlined" onClick={() => applyAlign("left")}>
              Left
            </Button>
            <Button size="small" variant="outlined" onClick={() => applyAlign("center")}>
              Center
            </Button>
            <Button size="small" variant="outlined" onClick={() => applyAlign("right")}>
              Right
            </Button>
            <Button size="small" variant="outlined" onClick={() => applyWidth(25)}>
              25%
            </Button>
            <Button size="small" variant="outlined" onClick={() => applyWidth(50)}>
              50%
            </Button>
            <Button size="small" variant="outlined" onClick={() => applyWidth(75)}>
              75%
            </Button>
            <Button size="small" variant="outlined" onClick={() => applyWidth(100)}>
              100%
            </Button>
          </Box>
        )}

        {/* 🟢 Featured Checkbox */}
        <FormControlLabel
          sx={{ mt: 2, mb: 1 }}
          control={
            <Checkbox
              checked={formData.is_featured}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  is_featured: e.target.checked,
                }))
              }
            />
          }
          label="Mark as Featured"
        />

        <Box sx={{ mt: 2 }}>
          <Button variant="outlined" component="label">
            Upload Blog Image
            <input
              hidden
              accept="image/*"
              type="file"
              onChange={handleBlogImageChange}
            />
          </Button>
          {formData.existingBlogImage && !formData.blog_image && (
            <Box mt={1}>
              <img src={formData.existingBlogImage} alt="blog" width={100} />
            </Box>
          )}
          {formData.blog_image && (
            <Box mt={1}>
              <img
                src={URL.createObjectURL(formData.blog_image)}
                alt="preview"
                width={100}
              />
            </Box>
          )}
        </Box>

        <Box sx={{ mt: 3 }}>
          {!!formData.existingImages?.length && (
            <Box mt={2} display="flex" gap={1.5} flexWrap="wrap">
              {formData.existingImages.map((img) => (
                <Box key={img.id} position="relative">
                  <img src={img.url} alt="existing" width={100} />
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => removeExistingImage(img.id)}
                    sx={{ position: "absolute", top: -8, right: -8 }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* ...rest unchanged */}
        {submissionError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {submissionError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setModalOpen(false)} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSaving}
          startIcon={
            isSaving ? <CircularProgress size={20} color="inherit" /> : null
          }
        >
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditBlog;
